import React, { useEffect, useRef } from 'react';
import { Routes, useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import toast, { Toaster } from 'react-hot-toast';
import { RootState } from './store/store';
import { clearCredentials } from './store/slices/authSlice';
import { useAppDispatch } from './store/hooks';
import './App.css';
import { renderRoutes } from './routes';

function App() {
  const theme = useSelector((state: RootState) => state.theme.theme);
  const token = useSelector((state: RootState) => state.auth.token);
  const tokenExpiresAt = useSelector((state: RootState) => state.auth.tokenExpiresAt);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const expiryTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    // Apply theme class to document root based on grid theme
    const isDark = theme === 'alpine-dark' || theme === 'quartz-dark';
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [theme]);

  // 2 saat sonra oturum süresi doldu uyarısı
  useEffect(() => {
    if (expiryTimerRef.current) {
      clearTimeout(expiryTimerRef.current);
      expiryTimerRef.current = null;
    }
    if (!token || tokenExpiresAt == null) return;

    const delay = tokenExpiresAt - Date.now();

    const showExpiredAndLogout = () => {
      toast.error(t('pages.login.sessionExpired'), { duration: 6000 });
      dispatch(clearCredentials());
      navigate('/login', { replace: true });
    };

    if (delay <= 0) {
      showExpiredAndLogout();
      return;
    }

    expiryTimerRef.current = setTimeout(showExpiredAndLogout, delay);
    return () => {
      if (expiryTimerRef.current) {
        clearTimeout(expiryTimerRef.current);
        expiryTimerRef.current = null;
      }
    };
  }, [token, tokenExpiresAt, dispatch, navigate, t]);

  return (
    <div className="App">
      <Toaster 
        position="top-right"
        toastOptions={{
          duration: 3000,
          style: {
            background: theme === 'alpine-dark' || theme === 'quartz-dark' ? '#1f2937' : '#ffffff',
            color: theme === 'alpine-dark' || theme === 'quartz-dark' ? '#ffffff' : '#000000',
          },
        }}
      />
      <Routes>
        {renderRoutes()}
      </Routes>
    </div>
  );
}

export default App;

